import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { getUserByToken, logoutSession } from "./authCrud";
import { announceSessionEnded, unregisterPushDevice } from "../../../../components/nativeSession";

export const actionTypes = {
  Login: "[Login] Action",
  Logout: "[Logout] Action",
  SessionEnded: "[Session Ended] Action",
  Register: "[Register] Action",
  UserRequested: "[Request User] Action",
  UserLoaded: "[Load User] Auth API",
  SetUser: "[Set User] Action",
  TokenRefreshed: "[Token Refreshed] Auth API"
};

const initialAuthState = {
  user: undefined,
  authToken: undefined
};

export const reducer = persistReducer(
  { storage, key: "anikev2", whitelist: ["authToken"] },
  (state = initialAuthState, action) => {
    switch (action.type) {
      case actionTypes.Login: {
        const { authToken } = action.payload;

        return { authToken, user: undefined };
      }

      case actionTypes.Register: {
        const { authToken } = action.payload;

        return { authToken, user: undefined };
      }

      // Local state is cleared only here: after an explicit sign-out has finished its server steps,
      // or when the refresh session was refused. A plain access-token refresh never reaches this.
      case actionTypes.SessionEnded: {
        return initialAuthState;
      }

      // Silent refresh: swap the short-lived access token, keep the signed-in user as is.
      case actionTypes.TokenRefreshed: {
        const { authToken } = action.payload;
        return { ...state, authToken };
      }

      case actionTypes.UserLoaded: {
        const { user } = action.payload;
        return { ...state, user };
      }

      case actionTypes.SetUser: {
        const { user } = action.payload;
        return { ...state, user };
      }

      default:
        return state;
    }
  }
);

export const actions = {
  login: authToken => ({ type: actionTypes.Login, payload: { authToken } }),
  register: authToken => ({
    type: actionTypes.Register,
    payload: { authToken }
  }),
  logout: () => ({ type: actionTypes.Logout }),
  sessionEnded: () => ({ type: actionTypes.SessionEnded }),
  tokenRefreshed: authToken => ({ type: actionTypes.TokenRefreshed, payload: { authToken } }),
  requestUser: user => ({
    type: actionTypes.UserRequested,
    payload: { user }
  }),
  fulfillUser: user => ({ type: actionTypes.UserLoaded, payload: { user } }),
  setUser: user => ({ type: actionTypes.SetUser, payload: { user } })
};

export function* saga() {
  yield takeLatest(actionTypes.Login, function* loginSaga() {
    yield put(actions.requestUser());
  });

  // Explicit sign-out, in order: unregister the push token while the access token is still usable,
  // revoke the refresh session, clear local state, then tell the native shell (see announceSessionEnded).
  yield takeLatest(actionTypes.Logout, function* logoutSaga() {
    yield call(unregisterPushDevice);
    try {
      yield call(logoutSession);
    } catch (e) {
      // offline or already signed out: the cookie expires on its own
    }
    yield put(actions.sessionEnded());
  });

  yield takeEvery(actionTypes.SessionEnded, function* sessionEndedSaga() {
    yield call(announceSessionEnded);
  });

  yield takeLatest(actionTypes.Register, function* registerSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.UserRequested, function* userRequested() {
    const { data: user } = yield getUserByToken();

    yield put(actions.fulfillUser(user));
  });
}
